export class AimgTokenManager {
  private cachedToken: string | null = null;
  private tokenPromise: Promise<string> | null = null;
  private viewerContext = 0;
  private readonly listeners = new Set<() => void>();
  private readonly issueToken: () => Promise<string>;

  constructor(issueToken: () => Promise<string>) {
    this.issueToken = issueToken;
  }

  /**
   * 新規発行(=フィンガープリント送信を伴うPOST /tokens)を一切トリガーせず、
   * 既に発行済みのtokenがあるかどうかだけを覗く。raw tokenは公開しない
   * (hover等の受動的なUIイベント起点でtoken発行を発生させたくない呼び出し元向け)。
   */
  hasToken(): boolean {
    return this.cachedToken !== null;
  }

  async get(): Promise<string> {
    if (this.cachedToken) return this.cachedToken;
    if (this.tokenPromise) return this.tokenPromise;

    this.tokenPromise = this.issueToken();
    try {
      const issuedToken = await this.tokenPromise;
      if (this.cachedToken !== issuedToken) {
        this.cachedToken = issuedToken;
        this.advanceViewerContext();
      }
      return this.cachedToken;
    } finally {
      this.tokenPromise = null;
    }
  }

  clear(token?: string): void {
    if (token !== undefined && this.cachedToken !== token) return;
    if (this.cachedToken === null) return;
    this.cachedToken = null;
    this.advanceViewerContext();
  }

  async refresh(expiredToken: string): Promise<string> {
    if (this.cachedToken && this.cachedToken !== expiredToken) {
      return this.cachedToken;
    }
    this.clear(expiredToken);
    return this.get();
  }

  getViewerContext(): number {
    return this.viewerContext;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return (): void => {
      this.listeners.delete(listener);
    };
  }

  private advanceViewerContext(): void {
    this.viewerContext += 1;
    for (const listener of this.listeners) listener();
  }
}

export function shouldRetryManagedToken(
  requiresToken: boolean,
  hasExplicitToken: boolean,
  retryCount: number,
  errorCode: string | undefined,
): boolean {
  return (
    requiresToken &&
    !hasExplicitToken &&
    retryCount === 0 &&
    (errorCode === "TOKEN_EXPIRED" || errorCode === "TOKEN_NOT_FOUND")
  );
}
